import React, { ReactNode } from 'react';
import { Button, Result } from 'antd';
import { useTenantNavigation } from '../hooks/useTenantNavigation.js';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // You can log the error to an error reporting service
    this.setState({
      error,
      errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} errorInfo={this.state.errorInfo} />;
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, errorInfo }) => {
  const { t } = useTranslation();
  const navigate = useTenantNavigation();

  const goHome = () => {
    // Navigate to appropriate home based on user role
    const userInfo = JSON.parse(localStorage.getItem('v_userInfo') || '{}');
    if (userInfo.isAdmin) {
      navigate('/app/dashboard');
    } else {
      navigate('/app/dashboard');
    }
  };

  const reloadPage = () => {
    window.location.reload();
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #FFE8E8 0%, #FFF3E0 100%)',
        backgroundImage: 'url(/Grid.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="max-w-2xl w-full">
        <Result
          status="error"
          title={t('Oops! Something went wrong')}
          subTitle={t('We encountered an unexpected error. Please try reloading the page or go back to the dashboard.')}
          extra={[
            <Button
              type="primary"
              key="reload"
              onClick={reloadPage}
              style={{
                backgroundColor: '#141BCB',
                borderColor: '#141BCB',
                height: '44px',
                fontSize: '16px',
                fontWeight: '500',
                borderRadius: '8px',
                padding: '0 32px',
                marginRight: '12px'
              }}
            >
              {t('Reload Page')}
            </Button>,
            <Button
              key="home"
              onClick={goHome}
              style={{
                height: '44px',
                fontSize: '16px',
                fontWeight: '500',
                borderRadius: '8px',
                padding: '0 32px'
              }}
            >
              {t('Go to Dashboard')}
            </Button>
          ]}
        >
          {process.env.NODE_ENV === 'development' && error && (
            <div className="text-left bg-gray-50 p-4 rounded-lg mt-4">
              <div className="text-sm font-semibold text-red-600 mb-2">
                {t('Error Details (Development Only):')}
              </div>
              <div className="text-xs text-gray-700 font-mono overflow-auto">
                <div className="mb-2">
                  <strong>{t('Error:')}</strong> {error.toString()}
                </div>
                {errorInfo && (
                  <div>
                    <strong>{t('Component Stack:')}</strong>
                    <pre className="mt-1 text-xs">{errorInfo.componentStack}</pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </Result>
      </div>
    </div>
  );
};

export default ErrorBoundary;
